import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useAuthApi } from "../../hooks/api/axiosInterceptor";

const Wrapper = styled.div`
    height: 100%;
    width: 100%;
    overflow: hidden;
    padding: 2rem;
    
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    text-align: left;
    white-space: pre-wrap;
    line-height: 1.6;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin: 20px 0;
    gap: 10px;
`;

const EditButton = styled.button`
    padding: 8px 20px;
    background-color: #4b70fd;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
    
    &:hover {
        background-color: #3a5fec;
    }
`;

const EditPanel = styled.div`
    width: 100%;
    margin-top: 20px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f9f9f9;
    display: ${props => props.show ? 'block' : 'none'};
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 150px;
    padding: 10px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.6;
    font-family: inherit;
`;

const SaveButton = styled.button`
    padding: 8px 20px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    margin-right: 10px;
    
    &:hover {
        background-color: #218838;
    }
`;

const CancelButton = styled.button`
    padding: 8px 20px;
    background-color: #666;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    
    &:hover {
        background-color: #555;
    }
`;

const StatusMessage = styled.div`
    margin-top: 10px;
    padding: 10px;
    border-radius: 4px;
    background-color: ${props => props.isError ? '#f8d7da' : '#d4edda'};
    color: ${props => props.isError ? '#721c24' : '#155724'};
    display: ${props => props.message ? 'block' : 'none'};
`;

// 기본 인사말 내용
const DEFAULT_GREETING = [
    "안녕하십니까, 중앙대학교 서울캠퍼스 67대 총학생회장 이민성, 부총학생회장 강혜선 중앙인 학우분들께 인사드립니다.",
    "학우분들의 관심과 응원이 모여 중앙대학교에 새로운 울림을 퍼트릴 [울림]이 출범할 수 있게 되었습니다. 학생자치에 관심을 가져주신 학우분들께 진심으로 감사의 말씀 드립니다.",
    "학우분들이 주신 믿음에 보답하겠습니다. 선거 기간 학우분들에게 약속드린 공약과 '울림'의 기조를 잊지 않고 늘 최선을 다하겠습니다.",
    "'울림'의 한 걸음마다 신중한 고민과 학우분들의 목소리를 담겠습니다. 가능과 불가능의 영역을 나누어 생각하지 않겠습니다. 학우분들을 대표하고, 그 목소리를 대변해야한다는 총학생회가 지켜내야할 가치를 잊지 않겠습니다.",
    "중앙대학교의 108년 역사와 전통을 이어 나가겠습니다. 중앙인의 목소리가 담겨, 중앙인 모두를'우리'로 어우러지게 하는 중앙대학교의 중심에 67대 총학생회가 있겠습니다.",
    "중앙인의 작은 울림이 모여 중앙대학교의, 세계로 향하는 새로운 울림이 퍼지길 꿈꿉니다.",
    "감사합니다.",
    "중앙대학교 서울캠퍼스 67대 총학생회장단 이민성 강혜선 올림"
];


const PresidentGreeting = () => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [paragraphs, setParagraphs] = useState([]);
    const [editText, setEditText] = useState('');
    const [statusMessage, setStatusMessage] = useState(null);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const checkAdminStatus = () => {
            const userRole = localStorage.getItem('user_role') || sessionStorage.getItem('user_role');
            setIsAdmin(userRole === 'admin');
        };

        checkAdminStatus();

        // Storage 이벤트 리스너
        const handleStorageChange = () => {
            checkAdminStatus();
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // 인사말 불러오기
    useEffect(() => {
        const fetchGreeting = async () => {
            setIsLoading(true);
            try {
                const response = await useAuthApi.get(`/president-greeting`);
                if (response.data.result === 'SUCCESS' && response.data.greeting) {
                    setParagraphs(response.data.greeting);
                } else {
                    console.error("인사말 불러오기 실패:", response.data.message);
                    setParagraphs(DEFAULT_GREETING);
                }
            } catch (error) {
                console.error("인사말 불러오기 오류:", error);
                setParagraphs(DEFAULT_GREETING);
            } finally {
                setIsLoading(false);
            }
        };

        fetchGreeting();
    }, []);

    // 편집 시작
    const handleEditClick = () => {
        setEditText(paragraphs.join('\n\n'));
        setIsEditing(true);
        setStatusMessage(null);
    };

    // 편집 취소
    const handleCancel = () => {
        setIsEditing(false);
        setStatusMessage(null);
    };

    // 변경 사항 저장
    const handleSave = async () => {
        if (!editText.trim()) {
            setStatusMessage("내용을 입력해주세요.");
            setIsError(true);
            return;
        }

        try {
            // 텍스트를 문단으로 분리
            const newParagraphs = editText
                .split('\n\n')
                .map(para => para.trim())
                .filter(para => para.length > 0);

            if (newParagraphs.length === 0) {
                setStatusMessage("내용을 입력해주세요.");
                setIsError(true);
                return;
            }

            // 서버에 인사말 업데이트 요청
            const response = await useAuthApi.post(`/president-greeting`, {
                greeting: newParagraphs
            });

            if (response.data.result === 'SUCCESS') {
                // 상태 업데이트
                setParagraphs(newParagraphs);
                setStatusMessage("인사말이 성공적으로 업데이트되었습니다.");
                setIsError(false);

                // 편집 모드 종료
                setTimeout(() => {
                    setIsEditing(false);
                    setStatusMessage(null);
                }, 2000);
            } else {
                throw new Error(response.data.message || "인사말 업데이트에 실패했습니다.");
            }
        } catch (error) {
            console.error("인사말 저장 오류:", error);
            setStatusMessage(error.message || "인사말 저장 중 오류가 발생했습니다.");
            setIsError(true);
        }
    };

    // 텍스트 변경 이벤트 핸들러
    const handleTextChange = (e) => {
        setEditText(e.target.value);
    };

    if (isLoading) {
        return <Wrapper>인사말을 불러오는 중입니다...</Wrapper>;
    }

    return (
        <Wrapper>
            {/* 인사말 표시 */}
            {paragraphs.map((paragraph, index) => (
                <Paragraph key={index}>{paragraph}</Paragraph>
            ))}

            {/* 관리자 수정 버튼 */}
            {isAdmin && !isEditing && (
                <ButtonContainer>
                    <EditButton onClick={handleEditClick}>수정</EditButton>
                </ButtonContainer>
            )}

            {/* 편집 패널 */}
            <EditPanel show={isEditing}>
                <h3>총학생회장단 인사말 수정</h3>
                <p>문단 구분은 빈 줄로 합니다. (엔터 두 번)</p>

                <TextArea
                    value={editText}
                    onChange={handleTextChange}
                    rows={10}
                    placeholder="인사말을 입력하세요..."
                />

                <StatusMessage isError={isError} message={statusMessage}>
                    {statusMessage}
                </StatusMessage>

                <div>
                    <SaveButton onClick={handleSave}>저장</SaveButton>
                    <CancelButton onClick={handleCancel}>취소</CancelButton>
                    {' '}
                </div>
            </EditPanel>
        </Wrapper>
    );
};

export default PresidentGreeting;